import React from 'react';
import { motion } from 'framer-motion';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface TabItem {
    label: React.ReactNode;
    value: string;
    icon?: React.ReactNode;
    count?: number;
    badge?: React.ReactNode;
    disabled?: boolean;
}

interface AnimatedTabsProps {
    tabs: TabItem[];
    activeTab: string;
    onTabChange?: (value: string) => void;
    layoutId?: string;
    className?: string;
    tabsListClassName?: string;
    triggerClassName?: string;
}

export const AnimatedTabs: React.FC<AnimatedTabsProps> = ({
    tabs,
    activeTab,
    onTabChange,
    layoutId = "active-pill",
    className,
    tabsListClassName = "mb-4",
    triggerClassName,
}) => {
    const tabsListRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const activeElement = tabsListRef.current?.querySelector('[data-state="active"]');
        if (activeElement) {
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [activeTab]);

    return (
        <div className={cn("w-fit max-w-full", className)}>
            <TabsList
                ref={tabsListRef}
                className={cn("flex w-full overflow-x-auto scrollbar-hide gap-2 p-2 h-auto min-h-[46px] rounded-[20px] whitespace-nowrap bg-muted justify-start", tabsListClassName)}
            >
                {tabs.map((tab) => (
                    <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        onClick={() => onTabChange?.(tab.value)}
                        disabled={tab.disabled}
                        className={cn(
                            "relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:bg-transparent transition-colors duration-200 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
                            tab.disabled && "opacity-50 cursor-not-allowed",
                            triggerClassName
                        )}
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            {tab.icon && (
                                <span className={cn(
                                    "w-4 h-4",
                                    activeTab === tab.value ? "text-primary" : "text-muted-foreground"
                                )}>
                                    {tab.icon}
                                </span>
                            )}
                            {tab.label}
                            {typeof tab.count === 'number' && (
                                <Badge
                                    variant="secondary"
                                    className={cn(
                                        "ml-1 px-1.5 py-0.5 text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full transition-colors duration-200",
                                        activeTab === tab.value
                                            ? " text-primary"
                                            : "bg-muted-foreground/20 text-muted-foreground"
                                    )}
                                >
                                    {tab.count}
                                </Badge>
                            )}
                            {tab.badge && (
                                <div className="ml-1">
                                    {tab.badge}
                                </div>
                            )}
                        </span>
                        {activeTab === tab.value && (
                            <motion.div
                                layoutId={layoutId}
                                className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                                transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                            />
                        )}
                    </TabsTrigger>
                ))}
            </TabsList>
        </div>
    );
};
